import { Field, FieldProps } from 'formik';
import { FormHelperText, Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloFormSection } from '@synergy/components-palo';
import { PoolFormattedMessage, PoolTextField, PoolTypography } from '@synergy/components';
import { SHOP_URL } from '@synergy/config';
import { fields } from '@synergy/utils';
import urlJoin from 'url-join';

import {
    PartialSupplier,
    PoolSupplierAutocompleteList,
} from '../../../../../../common/components/autocomplete-list/supplier-autocomplete-list/pool-supplier-autocomplete-list';
import { ROUTES } from '../../../../../sidebar-menu/navigation-drawer/routes';
import { SiteLink } from '../../../../components/site-link';
import { SupplierSpecialOffersOverviewFormValues } from '../models/supplier-special-offers-overview-form-values';
import { selectedLanguageSelector } from '../../../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

export const SupplierSpecialOffersDetailOverviewSupplierInfo = () => {
    const intl = useIntl();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const supplierId = useAppSelector(
        state => state.contentManagement.supplierSpecialOffers.detail.overview.supplierSpecialOffer?.supplier?.id
    );
    const fieldNames = fields<SupplierSpecialOffersOverviewFormValues>();

    return (
        <PaloFormSection title={<FormattedMessage id="app.contentManagement.supplierSpecialOffers.information" />}>
            <Grid container={true} spacing={2}>
                <Grid item={true} xs={12}>
                    <Field name={fieldNames.supplier}>
                        {({ field, form, meta }: FieldProps<PartialSupplier>) => (
                            <>
                                <PoolSupplierAutocompleteList
                                    label={<FormattedMessage id="app.freePosition.supplierAccount" />}
                                    onSelected={supplier => form.setFieldValue(field.name, supplier)}
                                    initialValue={field.value?.name}
                                    error={meta.touched && !!meta.error}
                                    disablePreventOverflow={true}
                                    required={true}
                                    isLocked={false}
                                    readOnly={selectedLanguage !== currentLocale}
                                    {...field}
                                />
                                {meta.touched && meta.error && (
                                    <FormHelperText error={true} sx={{ ml: 1.75 }}>
                                        <FormattedMessage id={meta.error} />
                                    </FormHelperText>
                                )}
                            </>
                        )}
                    </Field>
                </Grid>
                <Field name={fieldNames.path}>
                    {({ field, meta }: FieldProps<string>) => {
                        const searchParams = new URLSearchParams({
                            supplierIds: supplierId?.toString() ?? '',
                            hasSpecialPrice: 'true',
                        });
                        const urlLink = urlJoin(
                            SHOP_URL,
                            ROUTES.contentManagement.supplierSpecialOffers.shop,
                            field.value
                        );
                        const url = urlJoin(urlLink, '?' + searchParams.toString());
                        return (
                            <>
                                <Grid item={true} xs={12}>
                                    <PoolTextField
                                        {...field}
                                        fullWidth={true}
                                        label={
                                            <FormattedMessage id="app.contentManagement.supplierSpecialOffers.path" />
                                        }
                                        placeholder={intl.formatMessage({
                                            id: 'app.contentManagement.supplierSpecialOffers.pathPlaceholder',
                                        })}
                                        value={field.value ?? ''}
                                        required={true}
                                        disabled={selectedLanguage !== currentLocale}
                                        error={!!meta.touched && !!meta.error}
                                        helperText={
                                            meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                        }
                                    />
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <PoolFormattedMessage id="app.contentManagement.supplierSpecialOffers.link" />
                                    &nbsp;
                                    <SiteLink href={url} target="_blank">
                                        {urlLink}
                                    </SiteLink>
                                </Grid>
                            </>
                        );
                    }}
                </Field>
            </Grid>
        </PaloFormSection>
    );
};
